import React, { useEffect, useState, useRef } from 'react';
import { PlusCircle, Pencil, Search, Car } from 'lucide-react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ProtectedRoute from '../components/ProtectedRoute';
// Add jsPDF and html2canvas imports
import jsPDF from 'jspdf';

// Custom Date Input Component for Bulgarian format
const BulgarianDateInput = ({ value, onChange, placeholder, className, required }) => {
  const [displayValue, setDisplayValue] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateInputRef = useRef(null);

  // Convert ISO date to Bulgarian format
  const formatToBulgarian = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Convert Bulgarian format to ISO date
  const parseBulgarianDate = (bgDate) => {
    if (!bgDate) return "";
    const parts = bgDate.split(".");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split("T")[0];
      }
    }
    return "";
  };

  // Handle display value change
  const handleDisplayChange = (e) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);
    
    // Auto-format as user types
    if (newValue.length === 2 && !newValue.includes(".")) {
      setDisplayValue(newValue + ".");
    } else if (newValue.length === 5 && newValue.split(".").length === 2) {
      setDisplayValue(newValue + ".");
    }
    
    // Convert to ISO and call onChange
    const isoDate = parseBulgarianDate(newValue);
    if (isoDate) {
      onChange(isoDate);
    }
  };

  // Handle native date picker change
  const handleNativeDateChange = (e) => {
    const isoDate = e.target.value;
    onChange(isoDate);
    setDisplayValue(formatToBulgarian(isoDate));
  };

  // Update display value when value prop changes
  useEffect(() => {
    if (value) {
      setDisplayValue(formatToBulgarian(value));
    } else {
      setDisplayValue("");
    }
  }, [value]);

  return (
    <div className="relative">
      <input
        type="text"
        value={displayValue}
        onChange={handleDisplayChange}
        onFocus={() => setShowDatePicker(true)}
        placeholder={placeholder || "DD.MM.YYYY"}
        className={className}
        required={required}
      />
      {showDatePicker && (
        <input
          ref={dateInputRef}
          type="date"
          value={value || ""}
          onChange={handleNativeDateChange}
          className="absolute inset-0 opacity-0 cursor-pointer"
          onBlur={() => setShowDatePicker(false)}
        />
      )}
    </div>
  );
};

import html2canvas from 'html2canvas';
// Add CarDetailsPDF and PDFDownloadLink imports
import CarDetailsPDF from '../components/CarDetailsPDF';
import { PDFDownloadLink } from '@react-pdf/renderer';

const isExpiringSoon = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  const expiry = new Date(dateStr);
  const diff = (expiry - today) / (1000 * 60 * 60 * 24); // days
  return diff <= 30;
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatDateTime = (dateStr) => {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const AutoParkPageContent = () => {
  const { t } = useTranslation();
  const [cars, setCars] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    carModel: '',
    licensePlate: '',
    techInspectionExpiry: '',
    insuranceExpiry: '',
    vignetteExpiry: '',
    status: t('autoPark.statusActive'),
    driverName: '',
    carType: '',
    contactNumber: ''
  });
  const [showCarDetails, setShowCarDetails] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [carServices, setCarServices] = useState([]);
  const [carNotes, setCarNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [serviceInput, setServiceInput] = useState('');
  const [servicePrice, setServicePrice] = useState('');
  const [serviceType, setServiceType] = useState('regular');
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [isAddingService, setIsAddingService] = useState(false);
  const [loadingCarDetails, setLoadingCarDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('cars');
  
  // Machinery details state
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [showMachineDetails, setShowMachineDetails] = useState(false);
  const [loadingMachineDetails, setLoadingMachineDetails] = useState(false);
  
  // Machinery services and notes state
  const [machineServices, setMachineServices] = useState([]);
  const [machineNotes, setMachineNotes] = useState([]);
  const [machineNoteInput, setMachineNoteInput] = useState("");
  const [isAddingMachineNote, setIsAddingMachineNote] = useState(false);
  const [machineServiceInput, setMachineServiceInput] = useState('');
  const [machineServicePrice, setMachineServicePrice] = useState('');
  const [machineServiceType, setMachineServiceType] = useState('maintenance');
  const [isAddingMachineService, setIsAddingMachineService] = useState(false);
  
  const carDetailsRef = useRef(null);
  const exportRef = useRef(null);
  const machineDetailsRef = useRef(null);

  useEffect(() => {
    fetchCars();
    fetchMachinery();
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (showCarDetails || showMachineDetails) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCarDetails, showMachineDetails]);

  // Fetch car services and notes when car details modal opens
  useEffect(() => {
    if (showCarDetails && selectedCar?._id) {
      setLoadingCarDetails(true);
      Promise.all([
        fetch(`http://localhost:5001/api/cars/${selectedCar._id}/services`, { credentials: 'include' }),
        fetch(`http://localhost:5001/api/cars/${selectedCar._id}/notes`, { credentials: 'include' })
      ])
        .then(responses => Promise.all(responses.map(res => res.json())))
        .then(([servicesData, notesData]) => {
          setCarServices(servicesData);
          setCarNotes(notesData);
        })
        .catch(err => {
          console.error('Failed to fetch car details:', err);
          setCarServices([]);
          setCarNotes([]);
        })
        .finally(() => setLoadingCarDetails(false));
    }
  }, [showCarDetails, selectedCar?._id]);

  // Fetch machinery services and notes when machine details modal opens
  useEffect(() => {
    if (showMachineDetails && selectedMachine?._id) {
      setLoadingMachineDetails(true);
      Promise.all([
        fetch(`http://localhost:5001/api/machinery/${selectedMachine._id}/services`, { credentials: 'include' }),
        fetch(`http://localhost:5001/api/machinery/${selectedMachine._id}/notes`, { credentials: 'include' })
      ])
        .then(responses => Promise.all(responses.map(res => res.json())))
        .then(([servicesData, notesData]) => {
          setMachineServices(servicesData);
          setMachineNotes(notesData);
        })
        .catch(err => {
          console.error('Failed to fetch machine details:', err);
          setMachineServices([]);
          setMachineNotes([]);
        })
        .finally(() => setLoadingMachineDetails(false));
    }
  }, [showMachineDetails, selectedMachine?._id]);

  const fetchCars = async () => {
    try {
      const res = await fetch('http://localhost:5001/api/cars', { credentials: 'include' });
      const data = await res.json();
      setCars(data);
    } catch (err) {
      console.error('Failed to fetch cars', err);
    }
  };

  const fetchMachinery = async () => {
    try {
      console.log('🔍 Fetching machinery data...');
      const res = await fetch('http://localhost:5001/api/machinery', { credentials: 'include' });
      const data = await res.json();
      console.log('📊 Machinery data received:', data);
      console.log('📊 Machinery data type:', typeof data);
      console.log('📊 Machinery data length:', Array.isArray(data) ? data.length : 'Not an array');
      setMachinery(data);
    } catch (err) {
      console.error('❌ Failed to fetch machinery:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      const method = editId ? 'PATCH' : 'POST';
      const url = editId ? `http://localhost:5001/api/cars/${editId}` : 'http://localhost:5001/api/cars';
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        credentials: 'include'
      });
      fetchCars();
      setShowModal(false);
      setEditId(null);
      setFormData({
        carModel: '', licensePlate: '', techInspectionExpiry: '', insuranceExpiry: '', vignetteExpiry: '',
        status: t('autoPark.statusActive'), driverName: '', carType: '', contactNumber: ''
      });
    } catch (err) {
      console.error('Failed to save car', err);
    }
  };

  const handleEdit = (car) => {
    const {
      carModel, licensePlate, techInspectionExpiry, insuranceExpiry, vignetteExpiry,
      status, driverName, carType, contactNumber
    } = car;

    setFormData({
      carModel: carModel || '',
      licensePlate: licensePlate || '',
      techInspectionExpiry: techInspectionExpiry?.slice(0, 10) || '',
      insuranceExpiry: insuranceExpiry?.slice(0, 10) || '',
      vignetteExpiry: vignetteExpiry?.slice(0, 10) || '',
      status: status || t('autoPark.statusActive'),
      driverName: driverName || '',
      carType: carType || '',
      contactNumber: contactNumber || ''
    });
    setEditId(car._id);
    setShowModal(true);
  };

  const filteredCars = cars.filter(car => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = (
      car.carModel?.toLowerCase().includes(q) ||
      car.licensePlate?.toLowerCase().includes(q) ||
      car.driverName?.toLowerCase().includes(q)
    );
    
    const matchesStatus = statusFilter === 'all' || car.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    if (showCarDetails && selectedCar?._id) {
      setLoadingCarDetails(true);
      fetch(`http://localhost:5001/api/cars/${selectedCar._id}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          setSelectedCar(data);
          setCarServices(data.services || []);
          setCarNotes(data.notes || []);
        })
        .catch(err => {
          console.error('Failed to fetch car details:', err);
        })
        .finally(() => setLoadingCarDetails(false));
    }
  }, [showCarDetails, selectedCar?._id]);

  const handleAddService = async () => {
    if (!serviceInput.trim()) return;
    
    if (serviceType !== 'regular' && !newExpiryDate) {
      alert('Please select a new expiry date for document renewal');
      return;
    }

    try {
      const priceNum = parseFloat(servicePrice);
      const serviceData = {
        description: serviceInput,
        price: isNaN(priceNum) ? 0 : priceNum,
        serviceType: serviceType,
        newExpiryDate: serviceType !== 'regular' ? newExpiryDate : null
      };

      const res = await fetch(`http://localhost:5001/api/cars/${selectedCar._id}/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(serviceData),
        credentials: 'include'
      });
      
      if (res.ok) {
        const newService = await res.json();
        setCarServices(prev => [...prev, newService]);
        
        const updatedCarRes = await fetch(`http://localhost:5001/api/cars/${selectedCar._id}`, { 
          credentials: 'include' 
        });
        
        if (updatedCarRes.ok) {
          const updatedCarData = await updatedCarRes.json();
          setSelectedCar(updatedCarData);
        }
        
        setServiceInput('');
        setServicePrice('');
        setServiceType('regular');
        setNewExpiryDate('');
        setIsAddingService(false);
        
        fetchCars();
      } else {
        const errorData = await res.json();
        alert(`Failed to add service: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to add service:', err);
      alert('Failed to add service. Please try again.');
    }
  };

  const handleAddNote = async () => {
    if (!noteInput.trim()) return;

    try {
      const noteData = {
        description: noteInput
      };

      const res = await fetch(`http://localhost:5001/api/cars/${selectedCar._id}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(noteData),
        credentials: "include"
      });
      
      if (res.ok) {
        const newNote = await res.json();
        setCarNotes(prev => [...prev, newNote]);
        setNoteInput("");
        setIsAddingNote(false);
      } else {
        const errorData = await res.json();
        alert(`Failed to add note: ${errorData.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Failed to add note:", err);
      alert("Failed to add note. Please try again.");
    }
  };

  const resetNoteForm = () => {
    setNoteInput("");
    setIsAddingNote(false);
  };

  const resetServiceForm = () => {
    setServiceInput('');
    setServicePrice('');
    setServiceType('regular');
    setNewExpiryDate('');
    setIsAddingService(false);
  };

  // Machinery service and note functions
  const handleAddMachineService = async () => {
    if (!machineServiceInput.trim() || !machineServicePrice.trim()) return;
    
    try {
      const serviceData = {
        description: machineServiceInput.trim(),
        cost: parseFloat(machineServicePrice),
        serviceType: machineServiceType,
        date: new Date().toISOString()
      };
      
      const res = await fetch(`http://localhost:5001/api/machinery/${selectedMachine._id}/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(serviceData)
      });
      
      if (res.ok) {
        const newService = await res.json();
        setMachineServices(prev => [...prev, newService]);
        setMachineServiceInput('');
        setMachineServicePrice('');
        setMachineServiceType('maintenance');
        setIsAddingMachineService(false);
        
        // Refresh machinery data
        fetchMachinery();
      } else {
        const errorData = await res.json();
        alert(`Failed to add machine service: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to add machine service:', err);
      alert('Failed to add machine service. Please try again.');
    }
  };

  const handleAddMachineNote = async () => {
    if (!machineNoteInput.trim()) return;

    try {
      const noteData = {
        description: machineNoteInput
      };

      const res = await fetch(`http://localhost:5001/api/machinery/${selectedMachine._id}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(noteData),
        credentials: "include"
      });
      
      if (res.ok) {
        const newNote = await res.json();
        setMachineNotes(prev => [...prev, newNote]);
        setMachineNoteInput("");
        setIsAddingMachineNote(false);
        
        // Refresh machinery data
        fetchMachinery();
      } else {
        const errorData = await res.json();
        alert(`Failed to add machine note: ${errorData.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Failed to add machine note:", err);
      alert("Failed to add machine note. Please try again.");
    }
  };

  const resetMachineNoteForm = () => {
    setMachineNoteInput("");
    setIsAddingMachineNote(false);
  };

  const resetMachineServiceForm = () => {
    setMachineServiceInput('');
    setMachineServicePrice('');
    setMachineServiceType('maintenance');
    setIsAddingMachineService(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('autoPark.carsAndMachinery')}</h1>
          <p className="text-gray-600 mt-2">{t('autoPark.manageVehicleFleet')}</p>
        </div>
        <button
          onClick={() => {
            setFormData({
              carModel: '', licensePlate: '', techInspectionExpiry: '', insuranceExpiry: '', vignetteExpiry: '',
              status: t('autoPark.statusActive'), driverName: '', carType: '', contactNumber: ''
            });
            setEditId(null);
            setShowModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
        >
          <PlusCircle size={20} />
          {activeTab === 'machinery' ? t('autoPark.addNewMachine') : t('autoPark.addNewCar')}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('cars')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'cars'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Car size={16} />
              {t('autoPark.cars')}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('machinery')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'machinery'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Car size={16} />
              {t('autoPark.machinery')}
            </div>
          </button>
        </nav>
      </div>

      {/* Cars Tab */}
      {activeTab === 'cars' && (
        <div className="space-y-6">
          {/* Search and Status Filter */}
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={t('autoPark.searchCars')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="relative w-32">
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-blue-500 text-sm"
              >
                <option value="all">{t('autoPark.allStatuses')}</option>
                <option value="Active">{t('autoPark.active')}</option>
                <option value="Maintenance">{t('autoPark.maintenance')}</option>
                <option value="Repair">{t('autoPark.repair')}</option>
                <option value="Inactive">{t('autoPark.inactive')}</option>
                <option value="Retired">{t('autoPark.retired')}</option>
              </select>
            </div>
          </div>

          {/* Cars Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.model')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.plate')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.driver')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.type')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.insurance')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.vignette')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.techInsp')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.contact')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCars.map((car) => (
                    <tr
                      key={car._id}
                      className="hover:bg-gray-50 transition cursor-pointer"
                      onClick={e => {
                        if (e.target.closest('button')) return;
                        setSelectedCar(car);
                        setShowCarDetails(true);
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{car.carModel}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{car.licensePlate}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{car.driverName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{car.carType}</td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isExpiringSoon(car.insuranceExpiry) ? 'text-red-600 font-bold' : 'text-gray-900'}`}>{car.insuranceExpiry ? formatDate(car.insuranceExpiry) : ''}</td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isExpiringSoon(car.vignetteExpiry) ? 'text-red-600 font-bold' : 'text-gray-900'}`}>{car.vignetteExpiry ? formatDate(car.vignetteExpiry) : ''}</td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isExpiringSoon(car.techInspectionExpiry) ? 'text-red-600 font-bold' : 'text-gray-900'}`}>{car.techInspectionExpiry ? formatDate(car.techInspectionExpiry) : ''}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          (car.status === 'Active' || car.status === 'active') ? 'bg-green-100 text-green-800' :
                          (car.status === 'Maintenance' || car.status === 'maintenance') ? 'bg-yellow-100 text-yellow-800' :
                          (car.status === 'Repair' || car.status === 'repair') ? 'bg-red-100 text-red-800' :
                          (car.status === 'Inactive' || car.status === 'inactive') ? 'bg-gray-100 text-gray-800' :
                          (car.status === 'Retired' || car.status === 'retired') ? 'bg-gray-100 text-gray-800' :
                          'bg-blue-100 text-blue-800' // Default blue for any other status
                        }`}>
                          {car.status ? t(`autoPark.status${car.status.charAt(0).toUpperCase() + car.status.slice(1).toLowerCase()}`) : t('autoPark.active')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{car.contactNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button onClick={() => handleEdit(car)} className="text-blue-600 hover:text-blue-900">
                          <Pencil className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredCars.length === 0 && (
              <div className="text-center py-12">
                <Car className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('autoPark.noCarsFound')}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery || statusFilter !== 'all'
                    ? t('autoPark.tryAdjustingSearch')
                    : t('autoPark.getStartedAddingCar')
                  }
                </p>
              </div>
            )}
          </div>
          <div className="text-sm text-gray-600 font-semibold">{t('autoPark.totalCars')}: {filteredCars.length}</div>
        </div>
      )}

      {/* Machinery Tab */}
      {activeTab === 'machinery' && (
        <div className="space-y-6">
          {console.log('🔍 Rendering Machinery tab with machinery:', machinery)}
          {console.log('🔍 Machinery length:', machinery.length)}
          {/* Search and Status Filter */}
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={t('autoPark.searchMachinery')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="relative w-32">
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-blue-500 text-sm"
              >
                <option value="all">{t('autoPark.allStatuses')}</option>
                <option value="Active">{t('autoPark.statusActive')}</option>
                <option value="Maintenance">{t('autoPark.statusMaintenance')}</option>
                <option value="Repair">{t('autoPark.statusRepair')}</option>
                <option value="Inactive">{t('autoPark.statusInactive')}</option>
                <option value="Retired">{t('autoPark.statusRetired')}</option>
              </select>
            </div>
          </div>

          {/* Machinery Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.machineName')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.type')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.operator')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.location')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.nextMaintenance')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {machinery.filter(machine => {
                    if (!searchQuery && statusFilter === 'all') return true; // Show all if no filters
                    
                    const q = searchQuery.toLowerCase();
                    const matchesSearch = !searchQuery || (
                      machine.machineName?.toLowerCase().includes(q) ||
                      machine.machineType?.toLowerCase().includes(q) ||
                      machine.operatorName?.toLowerCase().includes(q) ||
                      machine.location?.toLowerCase().includes(q)
                    );
                    
                    const matchesStatus = statusFilter === 'all' || machine.status === statusFilter;
                    
                    return matchesSearch && matchesStatus;
                  }).map((machine) => (
                    <tr
                      key={machine._id}
                      className="hover:bg-gray-50 transition cursor-pointer"
                      onClick={e => {
                        if (e.target.closest('button')) return;
                        setSelectedMachine(machine);
                        setShowMachineDetails(true);
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{machine.machineName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{machine.machineType}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{machine.operatorName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{machine.location}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          machine.status === 'Active' ? 'bg-green-100 text-green-800' :
                          machine.status === 'Maintenance' ? 'bg-yellow-100 text-yellow-800' :
                          machine.status === 'Repair' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {machine.status ? t(`autoPark.status${machine.status.charAt(0).toUpperCase() + machine.status.slice(1).toLowerCase()}`) : t('autoPark.statusActive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {machine.nextMaintenance ? new Date(machine.nextMaintenance).toLocaleDateString('bg-BG') : 'Not set'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button className="text-blue-600 hover:text-blue-900">
                          <Pencil className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {machinery.filter(machine => {
              if (!searchQuery && statusFilter === 'all') return true; // Show all if no filters
              
              const q = searchQuery.toLowerCase();
              const matchesSearch = !searchQuery || (
                machine.machineName?.toLowerCase().includes(q) ||
                machine.machineType?.toLowerCase().includes(q) ||
                machine.operatorName?.toLowerCase().includes(q) ||
                machine.location?.toLowerCase().includes(q)
              );
              
              const matchesStatus = statusFilter === 'all' || machine.status === statusFilter;
              
              return matchesSearch && matchesStatus;
            }).length === 0 && (
              <div className="text-center py-12">
                <Car className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('autoPark.noMachineryFound')}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery || statusFilter !== 'all'
                    ? t('autoPark.tryAdjustingSearch')
                    : t('autoPark.getStartedAddingMachinery')
                  }
                </p>
              </div>
            )}
          </div>
          <div className="text-sm text-gray-600 font-semibold">{t('autoPark.totalMachinery')}: {machinery.length}</div>
        </div>
      )}

      {/* Modals */}
      <>
        {/* Add/Edit Car Modal */}
        {showModal && (
          <>
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-16 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                          {editId ? t('autoPark.editCar') : t('autoPark.addNewCar')}
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          {Object.entries(formData).map(([key, value]) => {
                            const isDateField = ['techInspectionExpiry', 'insuranceExpiry', 'vignetteExpiry'].includes(key);
                            const placeholderLabels = {
                              carModel: t('autoPark.carModel'),
                              licensePlate: t('autoPark.licensePlate'),
                              techInspectionExpiry: t('autoPark.techInspectionExpiry'),
                              insuranceExpiry: t('autoPark.insuranceExpiry'),
                              vignetteExpiry: t('autoPark.vignetteExpiry'),
                              status: t('autoPark.status'),
                              driverName: t('autoPark.driverName'),
                              carType: t('autoPark.carType'),
                              contactNumber: t('autoPark.contactNumber')
                            };

                            if (key === 'status') {
                              return (
                                <div key={key} className="col-span-1">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {placeholderLabels[key] || key}
                                  </label>
                                  <select
                                    value={value}
                                    onChange={e => setFormData({ ...formData, [key]: e.target.value })}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                  >
                                    <option value="Active">{t('autoPark.statusActive')}</option>
                                    <option value="Maintenance">{t('autoPark.statusMaintenance')}</option>
                                    <option value="Repair">{t('autoPark.statusRepair')}</option>
                                    <option value="Inactive">{t('autoPark.statusInactive')}</option>
                                    <option value="Retired">{t('autoPark.statusRetired')}</option>
                                  </select>
                                </div>
                              );
                            }

                            return (
                              <div key={key} className="col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {placeholderLabels[key] || key}
                                </label>
                                <input
                                  type={isDateField ? 'date' : 'text'}
                                  value={value}
                                  onChange={e => setFormData({ ...formData, [key]: e.target.value })}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      onClick={handleSubmit}
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                    >
                      {t('autoPark.save')}
                    </button>
                    <button
                      onClick={() => setShowModal(false)}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    >
                      {t('autoPark.cancel')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Car Details Modal */}
        {showCarDetails && selectedCar && (
          <>
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-40" />
            <div className="fixed inset-0 z-50 flex items-start pt-20">
              <div className="w-full">
                <div className="relative transform overflow-hidden bg-white text-left shadow-xl transition-all max-h-[calc(100vh-5rem)]" ref={carDetailsRef}>
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4 max-h-[calc(100vh-6rem)] overflow-y-auto">
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                        {/* Header with close button */}
                        <div className="flex items-center justify-between mb-6 sticky top-0 bg-white pb-2">
                          <h3 className="text-2xl font-bold text-gray-900">{t('autoPark.carInformation')}</h3>
                          <button
                            onClick={() => setShowCarDetails(false)}
                            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 hover:bg-gray-100 p-1"
                          >
                            <X className="h-6 w-6" />
                          </button>
                        </div>
                        <div ref={exportRef}>
                          {loadingCarDetails ? (
                            <div className="text-center py-8">Loading...</div>
                          ) : (
                            <>
                              <div className="grid grid-cols-3 gap-4 mb-6">
                                <div className="bg-gray-50 p-3 rounded-md">
                                  <span className="text-sm font-medium text-gray-500">{t('autoPark.model')}</span>
                                  <p className="text-sm text-gray-900">{selectedCar.carModel}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-md">
                                  <span className="text-sm font-medium text-gray-500">{t('autoPark.plate')}</span>
                                  <p className="text-sm text-gray-900">{selectedCar.licensePlate}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-md">
                                  <span className="text-sm font-medium text-gray-500">{t('autoPark.driver')}</span>
                                  <p className="text-sm text-gray-900">{selectedCar.driverName}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-md">
                                  <span className="text-sm font-medium text-gray-500">{t('autoPark.contact')}</span>
                                  <p className="text-sm text-gray-900">{selectedCar.contactNumber}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-md">
                                  <span className="text-sm font-medium text-gray-500">{t('autoPark.type')}</span>
                                  <p className="text-sm text-gray-900">{selectedCar.carType}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-md">
                                  <span className="text-sm font-medium text-gray-500">{t('autoPark.status')}</span>
                                  <div className="mt-1">
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      (selectedCar.status === 'Active' || selectedCar.status === 'active') ? 'bg-green-100 text-green-800' :
                                      (selectedCar.status === 'Maintenance' || selectedCar.status === 'maintenance') ? 'bg-yellow-100 text-yellow-800' :
                                      (selectedCar.status === 'Repair' || selectedCar.status === 'repair') ? 'bg-red-100 text-red-800' :
                                      (selectedCar.status === 'Inactive' || selectedCar.status === 'inactive') ? 'bg-gray-100 text-gray-800' :
                                      (selectedCar.status === 'Retired' || selectedCar.status === 'retired') ? 'bg-gray-100 text-gray-800' :
                                      'bg-blue-100 text-blue-800' // Default blue for any other status
                                    }`}>
                                      {selectedCar.status ? t(`autoPark.status${selectedCar.status.charAt(0).toUpperCase() + selectedCar.status.slice(1).toLowerCase()}`) : t('autoPark.statusActive')}
                                    </span>
                                  </div>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-md">
                                  <span className="text-sm font-medium text-gray-500">{t('autoPark.techInspExpiry')}</span>
                                  <p className={`text-sm ${isExpiringSoon(selectedCar.techInspectionExpiry) ? 'text-red-600 font-bold' : 'text-gray-900'}`}>
                                    {selectedCar.techInspectionExpiry ? formatDate(selectedCar.techInspectionExpiry) : t('autoPark.notSet')}
                                  </p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-md">
                                  <span className="text-sm font-medium text-gray-500">{t('autoPark.insuranceExpiry')}</span>
                                  <p className={`text-sm ${isExpiringSoon(selectedCar.insuranceExpiry) ? 'text-red-600 font-bold' : 'text-gray-900'}`}>
                                    {selectedCar.insuranceExpiry ? formatDate(selectedCar.insuranceExpiry) : t('autoPark.notSet')}
                                  </p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-md">
                                  <span className="text-sm font-medium text-gray-500">{t('autoPark.vignetteExpiry')}</span>
                                  <p className={`text-sm ${isExpiringSoon(selectedCar.vignetteExpiry) ? 'text-red-600 font-bold' : 'text-gray-900'}`}>
                                    {selectedCar.vignetteExpiry ? formatDate(selectedCar.vignetteExpiry) : t('autoPark.notSet')}
                                  </p>
                                </div>
                              </div>

                              {/* Services Section */}
                              <div className="mt-6">
                                                                <div className="flex items-center justify-between mb-4">
                                  <h3 className="text-lg font-semibold text-gray-900">{t('autoPark.services')}</h3>
                                   <button
                                   className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                   onClick={() => setIsAddingService(v => !v)}
                                 >
                                   {isAddingService ? t('autoPark.cancel') : t('autoPark.addService')}
                                 </button>
                                </div>
                                {isAddingService && (
                                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                            <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('autoPark.serviceType')}</label>
                                         <select
                                           value={serviceType}
                                           onChange={(e) => setServiceType(e.target.value)}
                                           className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                         >
                                          <option value="regular">{t('autoPark.regularService')}</option>
                                          <option value="techInspection">{t('autoPark.techInspection')}</option>
                                          <option value="insurance">{t('autoPark.insurance')}</option>
                                          <option value="vignette">{t('autoPark.vignette')}</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('autoPark.description')}</label>
                                         <input
                                           type="text"
                                           className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                           placeholder={t('autoPark.serviceDescriptionPlaceholder')}
                                           value={serviceInput}
                                           onChange={e => setServiceInput(e.target.value)}
                                         />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('autoPark.price')}</label>
                                         <input
                                           type="number"
                                           className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                           placeholder="0.00"
                                           value={servicePrice}
                                           onChange={e => setServicePrice(e.target.value)}
                                           min="0"
                                           step="0.01"
                                         />
                                      </div>
                                      {serviceType !== 'regular' && (
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('autoPark.newExpiryDate')} *</label>
                                           <input
                                           type="date"
                                           className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                           value={newExpiryDate}
                                           onChange={e => setNewExpiryDate(e.target.value)}
                                           required
                                         />
                                        </div>
                                      )}
                                    </div>
                                                                        <div className="flex gap-2 mt-4">
                                       <button
                                         className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                         onClick={handleAddService}
                                       >
                                         {t('autoPark.addService')}
                                       </button>
                                        <button
                                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                          onClick={resetServiceForm}
                                        >
                                          {t('autoPark.reset')}
                                        </button>
                                    </div>
                                  </div>
                                )}
                                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-blue-100">
                                      <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">#</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.description')}</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">{t('autoPark.date')}</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">{t('autoPark.price')}</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {carServices.length === 0 ? (
                                        <tr>
                                          <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">{t('autoPark.noServicesYet')}</td>
                                        </tr>
                                      ) : (
                                        <>
                                          {carServices.map((service, idx) => (
                                            <tr key={service._id || idx} className="hover:bg-gray-50">
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-12">{idx + 1}</td>
                                              <td className="px-6 py-4 text-sm text-gray-900">{service.description}</td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-32">{formatDateTime(service.date)}</td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right w-20">{typeof service.price === 'number' ? service.price.toFixed(2) : ''}</td>
                                            </tr>
                                          ))}
                                                                                  <tr className="bg-gray-50">
                                          <td colSpan={3} className="px-6 py-4 text-right text-sm font-medium text-gray-900">{t('autoPark.total')}:</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right w-20">
                                            {carServices.reduce((sum, s) => sum + (typeof s.price === 'number' ? s.price : 0), 0).toFixed(2)}
                                          </td>
                                        </tr>
                                        </>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              {/* Notes Section */}
                              <div className="mt-6">
                                                                <div className="flex items-center justify-between mb-4">
                                  <h3 className="text-lg font-semibold text-gray-900">{t('autoPark.notes')}</h3>
                                   <button
                                   className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                   onClick={() => setIsAddingNote(v => !v)}
                                 >
                                   {isAddingNote ? t('autoPark.cancel') : t('autoPark.addNote')}
                                 </button>
                                </div>
                                                                {isAddingNote && (
                                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('autoPark.description')}</label>
                                       <textarea
                                         className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                         placeholder={t('autoPark.noteDescriptionPlaceholder')}
                                         value={noteInput}
                                         onChange={e => setNoteInput(e.target.value)}
                                         rows="3"
                                       />
                                    </div>
                                    <div className="flex gap-2 mt-4">
                                       <button
                                         className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                         onClick={handleAddNote}
                                       >
                                         {t('autoPark.addNote')}
                                       </button>
                                        <button
                                         className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                         onClick={resetNoteForm}
                                       >
                                         {t('autoPark.reset')}
                                       </button>
                                    </div>
                                  </div>
                                )}
                                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-blue-100">
                                      <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">#</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.description')}</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">{t('autoPark.date')}</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20 opacity-0">{t('autoPark.price')}</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {carNotes.length === 0 ? (
                                        <tr>
                                          <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">{t('autoPark.noNotesYet')}</td>
                                        </tr>
                                      ) : (
                                        carNotes.map((note, idx) => (
                                          <tr key={note._id || idx} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-12">{idx + 1}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{note.description}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-32">{formatDateTime(note.date)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right w-20 opacity-0"></td>
                                          </tr>
                                        ))
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </>
                          )}
                          
                          {/* Export to PDF Button */}
                          <div className="mt-6 pt-4 border-t">
                            <div className="flex justify-start">
                              <PDFDownloadLink
                                document={<CarDetailsPDF car={selectedCar} services={carServices} notes={carNotes} translations={{
                                  'autoPark.carInformation': t('autoPark.carInformation'),
                                  'autoPark.model': t('autoPark.model'),
                                  'autoPark.plate': t('autoPark.plate'),
                                  'autoPark.driver': t('autoPark.driver'),
                                  'autoPark.contact': t('autoPark.contact'),
                                  'autoPark.type': t('autoPark.type'),
                                  'autoPark.status': t('autoPark.status'),
                                  'autoPark.techInspExpiry': t('autoPark.techInspExpiry'),
                                  'autoPark.insuranceExpiry': t('autoPark.insuranceExpiry'),
                                  'autoPark.vignetteExpiry': t('autoPark.vignetteExpiry'),
                                  'autoPark.services': t('autoPark.services'),
                                  'autoPark.description': t('autoPark.description'),
                                  'autoPark.date': t('autoPark.date'),
                                  'autoPark.price': t('autoPark.price'),
                                  'autoPark.total': t('autoPark.total'),
                                  'autoPark.notes': t('autoPark.notes'),
                                  'autoPark.noServicesYet': t('autoPark.noServicesYet'),
                                  'autoPark.noNotesYet': t('autoPark.noNotesYet'),
                                  'autoPark.active': t('autoPark.active')
                                }} />}
                                fileName={`CarDetails_${selectedCar.licensePlate || ''}.pdf`}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                {({ loading }) => loading ? t('autoPark.preparingPDF') : t('autoPark.exportToPDF')}
                              </PDFDownloadLink>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Machinery Details Modal */}
        {showMachineDetails && selectedMachine && (
          <>
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-40" />
            <div className="fixed inset-0 z-50 flex items-start pt-20">
              <div className="w-full">
                <div className="relative transform overflow-hidden bg-white text-left shadow-xl transition-all max-h-[calc(100vh-5rem)]" ref={machineDetailsRef}>
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4 max-h-[calc(100vh-6rem)] overflow-y-auto">
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                        {/* Header with close button */}
                        <div className="flex items-center justify-between mb-6 sticky top-0 bg-white pb-2">
                          <h3 className="text-2xl font-bold text-gray-900">{t('autoPark.machineryInformation')}</h3>
                          <button
                            onClick={() => setShowMachineDetails(false)}
                            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 hover:bg-gray-100 p-1"
                          >
                            <X className="h-6 w-6" />
                          </button>
                        </div>
                        
                        {loadingMachineDetails ? (
                          <div className="text-center py-8">Loading...</div>
                        ) : (
                          <>
                            <div className="grid grid-cols-3 gap-4 mb-6">
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.machineName')}</span>
                                <p className="text-sm text-gray-900">{selectedMachine.machineName}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.type')}</span>
                                <p className="text-sm text-gray-900">{selectedMachine.machineType}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.serialNumber')}</span>
                                <p className="text-sm text-gray-900">{selectedMachine.serialNumber}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.manufacturer')}</span>
                                <p className="text-sm text-gray-900">{selectedMachine.manufacturer}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.model')}</span>
                                <p className="text-sm text-gray-900">{selectedMachine.model}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.year')}</span>
                                <p className="text-sm text-gray-900">{selectedMachine.year}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.fuelType')}</span>
                                <p className="text-sm text-gray-900">{selectedMachine.fuelType || t('autoPark.notSpecified')}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">Status</span>
                                <div className="mt-1">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    selectedMachine.status === 'Active' ? 'bg-green-100 text-green-800' :
                                    selectedMachine.status === 'Maintenance' ? 'bg-yellow-100 text-yellow-800' :
                                    selectedMachine.status === 'Repair' ? 'bg-red-100 text-red-800' :
                                    selectedMachine.status === 'Inactive' ? 'bg-gray-100 text-gray-800' :
                                    selectedMachine.status === 'Retired' ? 'bg-gray-100 text-gray-800' :
                                    'bg-blue-100 text-blue-800' // Default blue for any other status
                                  }`}>
                                    {selectedMachine.status ? t(`autoPark.status${selectedMachine.status.charAt(0).toUpperCase() + selectedMachine.status.slice(1).toLowerCase()}`) : t('autoPark.statusActive')}
                                  </span>
                                </div>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.operator')}</span>
                                <p className="text-sm text-gray-900">{selectedMachine.operatorName || t('autoPark.notAssigned')}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.contact')}</span>
                                <p className="text-sm text-gray-900">{selectedMachine.contactNumber || t('autoPark.notProvided')}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.location')}</span>
                                <p className="text-sm text-gray-900">{selectedMachine.location || t('autoPark.notSpecified')}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.lastMaintenance')}</span>
                                <p className="text-sm text-gray-900">{selectedMachine.lastMaintenance ? formatDate(selectedMachine.lastMaintenance) : t('autoPark.notRecorded')}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.nextMaintenance')}</span>
                                <p className={`text-sm ${selectedMachine.nextMaintenance && isExpiringSoon(selectedMachine.nextMaintenance) ? 'text-red-600 font-bold' : 'text-gray-900'}`}>
                                  {selectedMachine.nextMaintenance ? formatDate(selectedMachine.nextMaintenance) : t('autoPark.notScheduled')}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-sm font-medium text-gray-500">{t('autoPark.insuranceExpiry')}</span>
                                <p className={`text-sm ${selectedMachine.insuranceExpiry && isExpiringSoon(selectedMachine.insuranceExpiry) ? 'text-red-600 font-bold' : 'text-gray-900'}`}>
                                  {selectedMachine.insuranceExpiry ? formatDate(selectedMachine.insuranceExpiry) : t('autoPark.notSet')}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                                                <span className="text-sm font-medium text-gray-500">{t('autoPark.registrationExpiry')}</span>
                                <p className={`text-sm ${selectedMachine.registrationExpiry && isExpiringSoon(selectedMachine.registrationExpiry) ? 'text-red-600 font-bold' : 'text-gray-900'}`}>
                                  {selectedMachine.registrationExpiry ? formatDate(selectedMachine.registrationExpiry) : t('autoPark.notSet')}
                                </p>
                              </div>

                            </div>

                            {/* Services Section */}
                            <div className="mt-6">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">{t('autoPark.services')}</h3>
                                <button
                                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  onClick={() => setIsAddingMachineService(v => !v)}
                                >
                                  {isAddingMachineService ? t('autoPark.cancel') : t('autoPark.addService')}
                                </button>
                              </div>
                              {isAddingMachineService && (
                                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('autoPark.description')}</label>
                                      <input
                                        type="text"
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        placeholder={t('autoPark.serviceDescriptionPlaceholder')}
                                        value={machineServiceInput}
                                        onChange={e => setMachineServiceInput(e.target.value)}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('autoPark.cost')}</label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        placeholder="0.00"
                                        value={machineServicePrice}
                                        onChange={e => setMachineServicePrice(e.target.value)}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('autoPark.serviceType')}</label>
                                      <select
                                        value={machineServiceType}
                                        onChange={e => setMachineServiceType(e.target.value)}
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                      >
                                        <option value="maintenance">{t('autoPark.maintenance')}</option>
                                        <option value="repair">{t('autoPark.repair')}</option>
                                        <option value="inspection">{t('autoPark.inspection')}</option>
                                        <option value="upgrade">{t('autoPark.upgrade')}</option>
                                        <option value="other">{t('autoPark.other')}</option>
                                      </select>
                                    </div>
                                  </div>
                                  <div className="flex gap-2 mt-4">
                                    <button
                                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      onClick={handleAddMachineService}
                                    >
                                      {t('autoPark.addService')}
                                    </button>
                                    <button
                                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      onClick={resetMachineServiceForm}
                                    >
                                      {t('autoPark.reset')}
                                    </button>
                                  </div>
                                </div>
                              )}
                              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                                <table className="min-w-full divide-y divide-gray-200">
                                                                                                            <thead className="bg-blue-100">
                                        <tr>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.date')}</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.type')}</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.description')}</th>
                                        </tr>
                                      </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {machineServices.length === 0 ? (
                                      <tr>
                                        <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">{t('autoPark.noServicesRecordedYet')}</td>
                                      </tr>
                                    ) : (
                                      machineServices.map((service, idx) => (
                                        <tr key={service._id || idx} className="hover:bg-gray-50">
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(service.date)}</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{service.serviceType}</td>
                                          <td className="px-6 py-4 text-sm text-gray-900">{service.description}</td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* Notes Section */}
                            <div className="mt-6">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">{t('autoPark.notes')}</h3>
                                <button
                                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  onClick={() => setIsAddingMachineNote(v => !v)}
                                >
                                  {isAddingMachineNote ? t('autoPark.cancel') : t('autoPark.addNote')}
                                </button>
                              </div>
                              {isAddingMachineNote && (
                                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('autoPark.description')}</label>
                                    <textarea
                                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                      placeholder={t('autoPark.noteDescriptionPlaceholder')}
                                      value={machineNoteInput}
                                      onChange={e => setMachineNoteInput(e.target.value)}
                                      rows="3"
                                    />
                                  </div>
                                  <div className="flex gap-2 mt-4">
                                    <button
                                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      onClick={handleAddMachineNote}
                                    >
                                      {t('autoPark.addNote')}
                                    </button>
                                    <button
                                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      onClick={resetMachineNoteForm}
                                    >
                                      {t('autoPark.reset')}
                                    </button>
                                  </div>
                                </div>
                              )}
                              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-blue-100">
                                    <tr>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">#</th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('autoPark.description')}</th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">{t('autoPark.date')}</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {machineNotes.length === 0 ? (
                                      <tr>
                                        <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">{t('autoPark.noNotesYet')}</td>
                                      </tr>
                                    ) : (
                                      machineNotes.map((note, idx) => (
                                        <tr key={note._id || idx} className="hover:bg-gray-50">
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-12">{idx + 1}</td>
                                          <td className="px-6 py-4 text-sm text-gray-900">{note.description}</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-32">{formatDateTime(note.date)}</td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* Maintenance History Section */}
                            {selectedMachine.maintenanceHistory && selectedMachine.maintenanceHistory.length > 0 && (
                              <div className="mt-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Maintenance History</h3>
                                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-blue-100">
                                      <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performed By</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {selectedMachine.maintenanceHistory.map((maintenance, idx) => (
                                        <tr key={maintenance._id || idx} className="hover:bg-gray-50">
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(maintenance.date)}</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{maintenance.type}</td>
                                          <td className="px-6 py-4 text-sm text-gray-900">{maintenance.description}</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${maintenance.cost?.toFixed(2) || '0.00'}</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{maintenance.performedBy || 'Not specified'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </>
    </div>
  );
};

const AutoParkPage = () => {
  return (
    <ProtectedRoute pageKey="autopark">
      <AutoParkPageContent />
    </ProtectedRoute>
  );
};

export default AutoParkPage;
